import { FC, Fragment, memo, ReactNode, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Alert } from "@/components-v2/Alert";
import { Box, Grid, VBox } from "@/components-v2/Box";
import { CopyToClipboardButton } from "@/components-v2/Button";
import { Checkbox } from "@/components-v2/Checkbox";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { DateInput } from "@/components-v2/DateInput";
import { Dialog } from "@/components-v2/Dialog";
import { Spinner } from "@/components-v2/LoadingIndicator";
import { Select, SelectView } from "@/components-v2/Select";
import { Table } from "@/components-v2/Table";
import { Tag } from "@/components-v2/Tag";
import tableStyles from "@/components-v2/Table.module.css";
import { TextInput } from "@/components-v2/TextInput";
import { TMessage } from "@/i18n/tt";
import { useTranslation } from "@/stores/RootStoreContext";
import { isEnter } from "@/utils/keyboardEvents";
import { systemFieldLabels } from "./customFieldsDomain";
import {
  CellEditor,
  ImportGridCell,
  ImportGridColumn,
  ImportGridRow,
  ImportGridRows,
  ImportResultRow,
  sameGridCell,
  sameGridRow,
  SpreadsheetImportGridProps,
  SpreadsheetImportResult,
  tabSeparated,
} from "./spreadsheetImportDomain";

const doNotImport = "";

const estimatedRowHeight = 47;

const overscanRows = 20;

export const SpreadsheetImportFilters: FC<SpreadsheetImportGridProps & { rowsLabel: TMessage }> =
  observer((props) => {
    const { t } = useTranslation();
    return (
      <>
        <Box gap={"lg"} alignItems={"center"}>
          <Box fontWeight={"bold"}>{t(props.rowsLabel)}</Box>
          <Checkbox
            label={{ key: "entity-data.spreadsheet-errors-only" }}
            checked={props.errorsOnly}
            onChange={props.onToggleErrorsOnly}
          />
        </Box>
        <Box gap={"lg"} alignItems={"center"}>
          <Box fontWeight={"bold"}>{t("entity-data.spreadsheet-fields-to-update")}</Box>
          <Checkbox
            label={{ key: "entity-data.spreadsheet-selected-only" }}
            checked={props.selectedOnly}
            onChange={props.onToggleSelectedOnly}
          />
          <Box fg={"subtle"} alignItems={"center"} gap={"sm"} textSize={"sm"}>
            {props.isValidating && <Spinner />}
            {t({
              key: "entity-data.spreadsheet-summary",
              params: { count: props.rowCount, errors: props.errorCount },
            })}
          </Box>
        </Box>
      </>
    );
  });

export const ImportGrid: FC<SpreadsheetImportGridProps> = observer((props) => {
  const { t } = useTranslation();
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  return (
    <Box ref={setScrollElement} overflow={"auto"}>
      <Table variant={"EDITABLE"} inline className={"border-separate border-spacing-0"}>
        <thead className={"sticky top-0 z-2 bg-default"}>
          <tr>
            <th colSpan={2} className={"sticky left-0 z-3 bg-default"} />
            {props.gridColumns.map((column) => (
              <th key={column.index}>
                <Box border rounded overflow={"hidden"} fontWeight={"normal"} marginBottom={"sm"}>
                  <FieldPicker column={column} {...props} />
                </Box>
              </th>
            ))}
          </tr>
          <tr>
            <th className={"sticky left-0 z-3 w-24 min-w-24 max-w-24 bg-default"}>
              <Box textAlign={"left"}>{t("entity-data.spreadsheet-country-code")}</Box>
            </th>
            <th className={"sticky left-24 z-3 bg-default"}>
              <Box textAlign={"left"}>{t("entity-data.spreadsheet-org-number-short")}</Box>
            </th>
            {props.gridColumns.map((column) => (
              <th key={column.index}>
                <Box alignItems={"center"} gap={"sm"}>
                  <Box
                    textAlign={"left"}
                    fg={column.issue ? "error" : undefined}
                    tooltip={column.issue}
                  >
                    {column.header || "–"}
                  </Box>
                  {column.field?.tag && (
                    <Tag compact color={"neutral"} icon={"calendar"} label={column.field.tag} />
                  )}
                </Box>
              </th>
            ))}
          </tr>
        </thead>
        <GridBody
          scrollElement={scrollElement}
          rows={props.gridRows}
          countryCodes={props.countryCodes}
          fieldCountryCodes={props.fieldCountryCodes}
          actions={props}
        />
      </Table>
    </Box>
  );
});

const FieldPicker: FC<SpreadsheetImportGridProps & { column: ImportGridColumn }> = observer(
  ({ column, fieldsByKey, isTakenElsewhere, onSelectField }) => (
    <Select
      fullWidth
      value={column.selectedKey ?? doNotImport}
      items={[doNotImport, ...fieldsByKey.keys()]}
      idFn={(key) => key || "do-not-import"}
      labelFn={(key) =>
        fieldsByKey.get(key)?.label ?? { key: "entity-data.spreadsheet-do-not-import" }
      }
      sectionFn={(key) => fieldsByKey.get(key)?.section}
      disabledReasonFn={(key) =>
        key && isTakenElsewhere(key, column.index)
          ? { key: "entity-data.spreadsheet-field-taken" }
          : undefined
      }
      onSelect={(key) => onSelectField(column.index, key || undefined)}
    />
  ),
);

type RowActions = Pick<
  SpreadsheetImportGridProps,
  "onChangeCountryCode" | "onChangeOrgNo" | "onChangeCell"
>;

type GridBodyProps = {
  scrollElement: HTMLDivElement | null;
  rows: ImportGridRows;
  countryCodes: string[];
  fieldCountryCodes: string[];
  actions: RowActions;
};

const GridBody = ({
  scrollElement,
  rows,
  countryCodes,
  fieldCountryCodes,
  actions,
}: GridBodyProps) => {
  const virtualizer = useVirtualizer({
    count: rows.count,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimatedRowHeight,
    getItemKey: (index) => rows.rowNumberAt(index),
    overscan: overscanRows,
  });
  const visible = virtualizer.getVirtualItems();
  const spaceBefore = visible[0]?.start ?? 0;
  const spaceAfter = virtualizer.getTotalSize() - (visible[visible.length - 1]?.end ?? 0);
  return (
    <tbody>
      {spaceBefore > 0 && <tr style={{ height: spaceBefore }} />}
      {visible.map((item) => (
        <GridRow
          key={item.key}
          index={item.index}
          measureRef={virtualizer.measureElement}
          row={rows.at(item.index)}
          countryCodes={countryCodes}
          fieldCountryCodes={fieldCountryCodes}
          actions={actions}
        />
      ))}
      {spaceAfter > 0 && <tr style={{ height: spaceAfter }} />}
    </tbody>
  );
};

type GridRowProps = {
  index: number;
  measureRef: (element: HTMLTableRowElement | null) => void;
  row: ImportGridRow;
  countryCodes: string[];
  fieldCountryCodes: string[];
  actions: RowActions;
};

const GridRow = memo(
  function GridRow({
    index,
    measureRef,
    row,
    countryCodes,
    fieldCountryCodes,
    actions,
  }: GridRowProps) {
    const { t } = useTranslation();
    const orgNoIssue = row.issue && t(row.issue);
    return (
      <tr ref={measureRef} data-index={index}>
        <td className={"sticky left-0 z-1 w-24 min-w-24 max-w-24 bg-default"}>
          <CountryCell
            value={row.countryCode.trim()}
            countryCodes={countryCodes}
            onChange={(code) => actions.onChangeCountryCode(row.row, code)}
          />
        </td>
        <td
          className={"sticky left-24 z-1 bg-default"}
          data-width={"md"}
          data-error={orgNoIssue !== undefined}
        >
          <TextCell
            value={row.orgNo}
            issue={orgNoIssue}
            onChange={(orgNo) => actions.onChangeOrgNo(row.row, orgNo)}
          />
        </td>
        {row.cells.map((cell) => (
          <GridCell
            key={cell.column}
            cell={cell}
            countryCodes={fieldCountryCodes}
            onChange={(text) => actions.onChangeCell(row.row, cell.column, text)}
          />
        ))}
      </tr>
    );
  },
  (before, after) =>
    before.index === after.index &&
    before.countryCodes === after.countryCodes &&
    before.fieldCountryCodes === after.fieldCountryCodes &&
    sameGridRow(before.row, after.row),
);

type GridCellProps = {
  cell: ImportGridCell;
  countryCodes: string[];
  onChange: (text: string) => void;
};

const GridCell = memo(
  function GridCell({ cell, countryCodes, onChange }: GridCellProps) {
    const { t } = useTranslation();
    const issue = cell.issue && t(cell.issue);
    if (!cell.editor) {
      return (
        <td className={tableStyles.readonly} data-width={"md"}>
          <Box display={"block"} className={"max-w-48 truncate"} paddingX={"md"} paddingY={"sm"}>
            {cell.text || <>&nbsp;</>}
          </Box>
        </td>
      );
    }
    return (
      <td data-width={"md"} data-error={issue !== undefined}>
        <CellInput
          editor={cell.editor}
          value={cell.text}
          issue={issue}
          countryCodes={countryCodes}
          onChange={onChange}
        />
      </td>
    );
  },
  (before, after) =>
    before.countryCodes === after.countryCodes && sameGridCell(before.cell, after.cell),
);

type CellInputProps = {
  value: string;
  issue?: string;
  onChange: (value: string) => void;
};

const sameInput = (before: CellInputProps, after: CellInputProps) =>
  before.value === after.value && before.issue === after.issue;

const CellInput = ({
  editor,
  countryCodes,
  ...props
}: CellInputProps & { editor: CellEditor; countryCodes: string[] }) => {
  switch (editor.type) {
    case "select":
      return <OptionCell {...props} options={editor.options} />;
    case "country":
      return <CountryCell {...props} countryCodes={countryCodes} />;
    case "date":
      return <DateCell {...props} />;
    case "text":
      return <TextCell {...props} />;
  }
};

const EditOnFocus: FC<{
  issue?: string;
  view: ReactNode;
  editor: (close: () => void) => ReactNode;
  opensOnFocus?: boolean;
}> = ({ issue, view, editor, opensOnFocus = true }) => {
  const [isEditing, setEditing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const open = () => setEditing(true);
  const openAfterClick = () => {
    timer.current = setTimeout(open);
  };
  return isEditing ? (
    <>{editor(() => setEditing(false))}</>
  ) : (
    <Box
      className={"max-w-48"}
      border={issue ? "error" : undefined}
      tooltip={issue}
      htmlProps={
        opensOnFocus
          ? { onFocus: open }
          : {
              onClick: openAfterClick,
              onKeyDown: (event) => (isEnter(event) || event.key === " ") && openAfterClick(),
            }
      }
    >
      {view}
    </Box>
  );
};

const CellText: FC<{ text: string; placeholder?: string }> = ({ text, placeholder }) => (
  <Box
    display={"block"}
    grow
    className={"truncate"}
    paddingX={"md"}
    paddingY={"sm"}
    fg={text ? undefined : "subtle"}
    htmlProps={{ tabIndex: 0 }}
  >
    {text || placeholder || <>&nbsp;</>}
  </Box>
);

const TextEditor: FC<CellInputProps & { close: () => void }> = ({
  value,
  issue,
  onChange,
  close,
}) => {
  const [draft, setDraft] = useState(value);
  return (
    <TextInput
      autofocus
      value={draft}
      error={issue !== undefined}
      message={issue}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        onChange(draft);
        close();
      }}
    />
  );
};

const TextCell = memo(function TextCell(props: CellInputProps) {
  return (
    <EditOnFocus
      issue={props.issue}
      view={<CellText text={props.value} />}
      editor={(close) => <TextEditor {...props} close={close} />}
    />
  );
}, sameInput);

const DateEditor: FC<CellInputProps> = ({ value, issue, onChange }) => {
  const [draft, setDraft] = useState(value);
  return (
    <DateInput
      autofocus
      value={draft}
      error={issue !== undefined}
      message={issue}
      onInput={(event) => setDraft(event.target.value)}
      onChange={(params) => onChange(params.input)}
    />
  );
};

const DateCell = (props: CellInputProps) => {
  const { t } = useTranslation();
  return (
    <EditOnFocus
      issue={props.issue}
      view={<CellText text={props.value} placeholder={t("date-picker.placeholder")} />}
      editor={() => <DateEditor {...props} />}
    />
  );
};

const withCurrent = (items: string[], current: string): string[] => [
  "",
  ...(current === "" || items.includes(current) ? [] : [current]),
  ...items,
];

const optionLabel = (option: string) => ({ translated: option || "–" });

const matchingOption = (options: string[], value: string): string =>
  options.find((option) => option.trim().toLowerCase() === value.trim().toLowerCase()) ?? value;

const OptionCell = ({
  value: text,
  issue,
  options,
  onChange,
}: CellInputProps & { options: string[] }) => {
  const value = matchingOption(options, text);
  return (
    <EditOnFocus
      issue={issue}
      opensOnFocus={false}
      view={<SelectView value={value} labelFn={optionLabel} />}
      editor={(close) => (
        <Select
          autofocus
          fullWidth
          value={value}
          items={withCurrent(options, value)}
          idFn={(option) => option || "empty"}
          labelFn={optionLabel}
          error={issue !== undefined}
          message={issue}
          onSelect={onChange}
          onClose={close}
        />
      )}
    />
  );
};

const CountryCell = memo(
  function CountryCell({
    value,
    issue,
    countryCodes,
    onChange,
  }: CellInputProps & { countryCodes: string[] }) {
    const code = value.trim();
    return (
      <EditOnFocus
        issue={issue}
        opensOnFocus={false}
        view={<SelectView value={code} labelFn={optionLabel} />}
        editor={(close) => (
          <Select
            autofocus
            fullWidth
            value={code}
            items={withCurrent(countryCodes, code)}
            idFn={(option) => option || "empty"}
            labelFn={optionLabel}
            itemLabelFn={(option) =>
              countryCodes.includes(option)
                ? { type: "country", code: option }
                : optionLabel(option)
            }
            error={issue !== undefined}
            message={issue}
            onSelect={onChange}
            onClose={close}
          />
        )}
      />
    );
  },
  (before, after) => sameInput(before, after) && before.countryCodes === after.countryCodes,
);

type ResultField = {
  name: string;
  label: TMessage;
  value: (row: ImportResultRow) => string;
};

export const SpreadsheetImportResultDialog: FC<{
  result: SpreadsheetImportResult;
  onClose: () => void;
}> = observer(({ result, onClose }) => {
  const { t } = useTranslation();
  const fields: ResultField[] = [
    { name: "row", label: { key: "entity-data.import-row" }, value: (row) => `${row.row ?? ""}` },
    {
      name: "org-no",
      label: { key: "entity-data.spreadsheet-org-number" },
      value: (row) => row.orgNo ?? "",
    },
    {
      name: "company-name",
      label: systemFieldLabels.COMPANY_NAME,
      value: (row) => row.companyName ?? "",
    },
    {
      name: "column",
      label: { key: "entity-data.spreadsheet-column" },
      value: (row) => row.column ?? "",
    },
    {
      name: "field",
      label: { key: "entity-data.field-name" },
      value: (row) => (row.field ? t(row.field) : ""),
    },
    {
      name: "problem",
      label: { key: "entity-data.import-reason" },
      value: (row) => t(row.problem),
    },
    { name: "value", label: { key: "entity-data.value" }, value: (row) => row.value ?? "" },
  ];
  const columns: TableColumn<ImportResultRow>[] = fields.map(({ name, label, value }) => ({
    name,
    label,
    getValue: value,
    align: "left",
  }));
  const copyText = tabSeparated([
    fields.map(({ label }) => t(label)),
    ...result.rows.map((row) => fields.map(({ value }) => value(row))),
  ]);
  return (
    <Dialog
      title={t("entity-data.import-result-title")}
      onCancel={onClose}
      cancelLabel={t("close")}
      maxWidth={"lg"}
      fullscreen={result.rows.length > 0}
    >
      <VBox gap={"lg"}>
        {result.nothingImported && (
          <Alert type={"warning"} message={t("entity-data.spreadsheet-nothing-imported")} />
        )}
        <Grid cols={2} gap={"md"}>
          {result.counts.map((count) => (
            <Fragment key={count.key}>
              <Box fg={"subtle"}>{t(count.label)}</Box>
              <div>{count.value}</div>
            </Fragment>
          ))}
        </Grid>
        {result.rows.length === 0 ? (
          <Alert type={"success"} message={t("entity-data.import-no-problems")} />
        ) : (
          <VBox gap={"md"}>
            <Box alignItems={"center"} justifyContent={"space-between"}>
              <Box fontWeight={"bold"}>{t("entity-data.import-problems")}</Box>
              <CopyToClipboardButton text={copyText} label={{ key: "general.copy-to-clipboard" }} />
            </Box>
            <DataTable tableColumns={columns} tableData={result.rows} getId={(row) => row.id} />
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});
